import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Database, Download, Upload, FileText, FileSpreadsheet, AlertTriangle, CheckCircle2, Users, Crown, UserCog, Receipt, ClipboardList, Award, Wallet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPORT_ENTITIES = [
  { key: 'customers',   label: 'Customers',     icon: Users,         desc: 'All customers with spending, membership, last service' },
  { key: 'vips',        label: 'VIP Members',   icon: Crown,         desc: 'VIPs with promo codes, bonus totals, payout status' },
  { key: 'employees',   label: 'Employees',     icon: UserCog,       desc: 'Staff incl. role, dept, pay rate, hire date' },
  { key: 'invoices',    label: 'Invoices',      icon: Receipt,       desc: 'All invoices with status, customer, amount' },
  { key: 'estimates',   label: 'Estimates',     icon: ClipboardList, desc: 'All estimates with status, customer, amount' },
  { key: 'memberships', label: 'Memberships',   icon: Crown,         desc: 'Active + cancelled membership records' },
  { key: 'referrals',   label: 'Referral History', icon: Award,      desc: 'Every referral bonus (auto + manual)' },
  { key: 'payouts',     label: 'Payout History', icon: Wallet,       desc: 'Paid VIP referral payouts only' },
];

const SUPPORTS_ARCHIVE = new Set(['customers', 'vips', 'employees']);

const DataTools = () => {
  const [format, setFormat] = useState('csv');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [strategy, setStrategy] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { refreshHistory(); }, []);

  const refreshHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/import/customers/history`, { withCredentials: true });
      setHistory(data.rows || []);
    } catch { /* ignore */ }
  };

  const downloadFile = async (url, suggestedName) => {
    const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const handleExport = async (entity) => {
    setDownloading((d) => ({ ...d, [entity]: true }));
    try {
      const params = new URLSearchParams({ format });
      if (SUPPORTS_ARCHIVE.has(entity) && includeArchived) params.set('include_archived', 'true');
      const url = `${API}/admin/export/${entity}?${params.toString()}`;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      await downloadFile(url, `nexflow_${entity}_${today}.${format}`);
      toast.success(`${entity} exported`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to export ${entity}`);
    }
    setDownloading((d) => ({ ...d, [entity]: false }));
  };

  const handleTemplate = async (fmt) => {
    try {
      const url = `${API}/admin/import/customers/template?format=${fmt}`;
      await downloadFile(url, `nexflow_customers_import_template.${fmt}`);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handlePreview = async () => {
    if (!importFile) { toast.error('Choose a file first'); return; }
    const fd = new FormData();
    fd.append('file', importFile);
    try {
      const { data } = await axios.post(`${API}/admin/import/customers/preview`, fd, {
        withCredentials: true,
        // Let axios set the multipart boundary
      });
      setPreview(data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse file');
    }
  };

  const handleCommit = async () => {
    if (!preview?.import_token) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/admin/import/customers/commit`, {
        import_token: preview.import_token,
        strategy,
      }, { withCredentials: true });
      toast.success(`Import complete · ${data.created} created · ${data.updated} updated · ${data.skipped} skipped · ${data.failed} failed`);
      setPreviewOpen(false);
      setPreview(null);
      setImportFile(null);
      refreshHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const fmtPercent = (n, total) => total ? `${Math.round((n / total) * 100)}%` : '0%';

  return (
    <div data-testid="admin-data-tools-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Database className="w-7 h-7 text-[#D4AF37]" /> Data Tools
        </h2>
        <p className="text-[#A3A3A3]">Export NexFlow's business data anytime · Import customers from QuickBooks or other systems</p>
      </div>

      {/* Export Controls */}
      <section className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-lg font-bold text-white">Export Business Data</h3>
            <p className="text-[#A3A3A3] text-xs mt-0.5">All exports are streamed downloads. Choose format and click any entity.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger data-testid="export-format-select" className="bg-[#0A0A0A] border-[#262626] text-white w-32 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input
                data-testid="export-include-archived"
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="accent-[#D4AF37]"
              />
              Include archived
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {EXPORT_ENTITIES.map(({ key, label, icon: Icon, desc }) => (
            <div key={key} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-md flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-medium">{label}</p>
                  <p className="text-[#A3A3A3] text-xs mt-0.5 leading-snug">{desc}</p>
                </div>
              </div>
              <Button
                data-testid={`export-${key}-btn`}
                onClick={() => handleExport(key)}
                disabled={!!downloading[key]}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading[key] ? 'Exporting…' : `Export ${format.toUpperCase()}`}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Customer Import */}
      <section className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Import Customers</h3>
            <p className="text-[#A3A3A3] text-xs mt-0.5">Upload CSV or XLSX with columns: name, email, phone, address, notes</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="template-csv-btn" onClick={() => handleTemplate('csv')} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <FileText className="w-4 h-4 mr-2" /> CSV Template
            </Button>
            <Button data-testid="template-xlsx-btn" onClick={() => handleTemplate('xlsx')} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> XLSX Template
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-white text-xs uppercase tracking-wider block mb-2">Upload file</label>
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white bg-[#0A0A0A] border border-[#262626] rounded-md
                         file:mr-3 file:py-2.5 file:px-4 file:rounded-md file:border-0
                         file:bg-[#D4AF37] file:text-[#0A0A0A] file:font-medium hover:file:bg-[#F3E5AB]"
            />
            {importFile && (
              <p className="text-[#A3A3A3] text-xs mt-1.5">
                Selected: <span className="text-white">{importFile.name}</span> · {(importFile.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
          <Button
            data-testid="import-preview-btn"
            onClick={handlePreview}
            disabled={!importFile}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-50"
          >
            <Upload className="w-4 h-4 mr-2" /> Preview Import
          </Button>
        </div>
      </section>

      {/* Import History */}
      {history.length > 0 && (
        <section className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <h3 className="text-lg font-bold text-white mb-3">Recent Imports</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                <tr>
                  <th className="text-left px-3 py-2 text-[#A3A3A3]">File</th>
                  <th className="text-left px-3 py-2 text-[#A3A3A3]">Strategy</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Created</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Updated</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Skipped</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Failed</th>
                  <th className="text-left px-3 py-2 text-[#A3A3A3]">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.import_token} className="border-b border-[#262626] last:border-0">
                    <td className="px-3 py-2 text-white truncate max-w-[260px]">{h.file_name}</td>
                    <td className="px-3 py-2 text-[#A3A3A3] uppercase text-xs">{h.strategy}</td>
                    <td className="px-3 py-2 text-right text-green-400">{h.summary?.created || 0}</td>
                    <td className="px-3 py-2 text-right text-[#D4AF37]">{h.summary?.updated || 0}</td>
                    <td className="px-3 py-2 text-right text-[#A3A3A3]">{h.summary?.skipped || 0}</td>
                    <td className="px-3 py-2 text-right text-red-400">{h.summary?.failed || 0}</td>
                    <td className="px-3 py-2 text-[#525252] text-xs">{h.committed_at ? new Date(h.committed_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="import-preview-dialog" className="bg-[#141414] border-[#262626] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Preview</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">
              Review issues and duplicates, choose a strategy, then commit.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <SummaryTile label="Total" value={preview.counts.total} accent="text-white" />
                <SummaryTile label="Valid" value={preview.counts.valid} accent="text-green-400" suffix={fmtPercent(preview.counts.valid, preview.counts.total)} />
                <SummaryTile label="Duplicates" value={preview.counts.duplicate} accent="text-[#D4AF37]" />
                <SummaryTile label="Missing required" value={preview.counts.missing_required} accent="text-red-400" />
                <SummaryTile label="Invalid email" value={preview.counts.invalid_email} accent="text-red-400" />
                <SummaryTile label="Invalid phone" value={preview.counts.invalid_phone} accent="text-red-400" />
              </div>

              {/* Strategy */}
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4">
                <p className="text-white font-medium mb-2">Duplicate handling strategy</p>
                <div className="flex flex-col gap-2">
                  {[
                    ['skip',   'Skip duplicates',          'Leave existing customers untouched. Safest.'],
                    ['update', 'Update existing (merge)',  'Only fill in fields that are blank or new. Existing data is preserved.'],
                    ['create', 'Create duplicate anyway',  'Always create a new record (may produce duplicate emails — only use deliberately).'],
                  ].map(([val, label, hint]) => (
                    <label key={val} className="flex items-start gap-2 text-sm text-white cursor-pointer">
                      <input
                        type="radio"
                        name="strategy"
                        value={val}
                        checked={strategy === val}
                        onChange={(e) => setStrategy(e.target.value)}
                        data-testid={`strategy-${val}`}
                        className="accent-[#D4AF37] mt-1"
                      />
                      <span>
                        <span className="text-white font-medium">{label}</span>
                        <span className="text-[#A3A3A3] text-xs block mt-0.5">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Row preview */}
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-md overflow-x-auto max-h-[40vh]">
                <table className="w-full min-w-[760px] text-xs">
                  <thead className="bg-[#0A0A0A] border-b border-[#262626] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Row</th>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Name</th>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Email</th>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Phone</th>
                      <th className="text-left px-3 py-2 text-[#A3A3A3]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => {
                      const hasIssue = r.issues.length > 0;
                      const isDup = r.duplicate_of || r.duplicate_in_file;
                      return (
                        <tr key={r.row_number} className="border-b border-[#262626] last:border-0">
                          <td className="px-3 py-2 text-[#525252]">{r.row_number}</td>
                          <td className="px-3 py-2 text-white">{r.name || <span className="text-red-400">—</span>}</td>
                          <td className="px-3 py-2 text-[#A3A3A3]">{r.email || '—'}</td>
                          <td className="px-3 py-2 text-[#A3A3A3]">{r.phone || '—'}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {!hasIssue && !isDup && (
                                <Badge className="bg-green-900/30 text-green-400 border-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>
                              )}
                              {r.issues.map((iss) => (
                                <Badge key={iss} className="bg-red-900/30 text-red-400 border-red-700"><AlertTriangle className="w-3 h-3 mr-1" />{iss.replace('_',' ')}</Badge>
                              ))}
                              {r.duplicate_of && (
                                <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30">DUP by {r.duplicate_of.by}</Badge>
                              )}
                              {r.duplicate_in_file && (
                                <Badge className="bg-amber-900/30 text-amber-400 border-amber-700">DUP in file ({r.duplicate_in_file})</Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {preview.total_rows > preview.rows.length && (
                <p className="text-[#525252] text-xs">Showing first {preview.rows.length} of {preview.total_rows} rows. Commit processes all rows.</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={() => setPreviewOpen(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
                <Button
                  data-testid="import-commit-btn"
                  onClick={handleCommit}
                  disabled={importing}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                >
                  {importing ? 'Importing…' : `Commit Import (${strategy})`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryTile = ({ label, value, accent, suffix }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-white'}`}>{value}</p>
    {suffix && <p className="text-[#525252] text-xs mt-0.5">{suffix}</p>}
  </div>
);

export default DataTools;
